import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import PackageIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon';
import PalletIcon from '../../../../../../generic/icons/IconLib/PalletIcon';
import PencilIcon from '../../../../../../generic/icons/IconLib/PencilIcon';
import { CardRow } from '../../../../../../generic/ui/CardRow';
import IconTextField from '../../../../../../generic/ui/Input/IconTextField';
import { ScrollArea } from '../../../../../../generic/ui/Surface';
import { RadioSlider } from '../../../../../../generic/ui/Toggles/RadioSlider';
import { IconSlot, NeutralBtn, PrimaryName } from '../../Components/DriversCardParts';
import { PACK_TYPE_LABELS, TAB_ALL } from '../../helpers/packTypes';

/** Общая форма строки: у IPackPalletItem имя в Pack_Name, у IArizotLine — в Description. */
export interface IPackRow {
    id: string;
    name: string;
    /** Код Pack_Pallet: 'א' — аризот, 'מ' — миштахим. */
    type: string;
    qty: number;
    remark?: string;
}

interface Props {
    rows: IPackRow[];
    onQtyChange?: (qty: number, id: string) => void;
    onRemark?: (row: IPackRow) => void;
    readOnly?: boolean;
}

const PackagesPickList: React.FC<Props> = ({ rows, onQtyChange, onRemark, readOnly }) => {
    const [activeTab, setActiveTab] = useState<string>(TAB_ALL);

    const tabs = useMemo(() => {
        const types = Array.from(new Set(rows.map((row) => row.type.trim())));
        return [
            { id: TAB_ALL, label: TAB_ALL, badge: rows.length },
            ...types.map((type) => ({
                id: type,
                label: PACK_TYPE_LABELS[type] ?? type,
                badge: rows.filter((row) => row.type.trim() === type).length,
            })),
        ];
    }, [rows]);

    const render = useMemo(
        () => (activeTab === TAB_ALL ? rows : rows.filter((row) => row.type.trim() === activeTab)),
        [activeTab, rows],
    );

    const lastIndex = render.length - 1;

    return (
        <Container>
            <RadioSlider
                items={tabs}
                value={activeTab}
                onChange={setActiveTab}
                ariaLabel='סוג אריזה'
            />

            <ScrollArea>
                {render.map((row, idx) => (
                    <CardRow
                        key={row.id}
                        $columns='auto minmax(0, 1fr) 6rem auto'
                        $mobileColumns='auto minmax(0, 1fr) 5rem auto'
                        $minHeight='auto'
                        $hover={false}
                        $isLast={idx === lastIndex}
                    >
                        <CardRow.Cell>
                            <IconSlot $muted>
                                {row.type.trim() === 'א' ? <PackageIcon /> : <PalletIcon />}
                            </IconSlot>
                        </CardRow.Cell>

                        <CardRow.Cell>
                            <PrimaryName>{row.name}</PrimaryName>
                        </CardRow.Cell>

                        <CardRow.Cell>
                            <IconTextField
                                type='number'
                                value={row.qty || ''}
                                onChange={(e) => onQtyChange?.(Number(e.target.value), row.id)}
                                disabled={readOnly}
                                placeholder='0'
                                min={0}
                            />
                        </CardRow.Cell>

                        <CardRow.Cell $align='end'>
                            {onRemark && (
                                <NeutralBtn
                                    type='button'
                                    $active={!!row.remark?.trim()}
                                    onClick={() => onRemark(row)}
                                    aria-label='הערה'
                                    title='הערה'
                                >
                                    <PencilIcon />
                                </NeutralBtn>
                            )}
                        </CardRow.Cell>
                    </CardRow>
                ))}
            </ScrollArea>
        </Container>
    );
};

export default PackagesPickList;

const Container = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    direction: rtl;
`;
